import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { Switch } from "ui/switch";
import { TextField } from "ui/text-field";

import Icons from "assets";
import { TouchpointCreateRequestType } from "generated/schemas";

import { useValidateProgramTemplateName } from "containers/admin/clients/client/program-templates-page/hooks/use-validate-program-template-name";
import type { AddNewProgramTemplateFormData } from "containers/admin/clients/client/program-templates-page/program-templates-page.types";
import { LoadingButtonState } from "ui/button";

interface AddNewProgramTemplateModalProps {
  isOpen: boolean;
  templateType: TouchpointCreateRequestType;
  onClose: () => void;
  onSubmit?: (
    data: AddNewProgramTemplateFormData,
    templateType: TouchpointCreateRequestType,
  ) => void;
  submitState?: LoadingButtonState;
}

const getTemplateModalConfig = (templateType: TouchpointCreateRequestType) => {
  switch (templateType) {
    case TouchpointCreateRequestType.EMAIL:
      return {
        Icon: Icons.Email,
        title: "Create email template",
      };
    case TouchpointCreateRequestType.OTHER:
      return {
        Icon: Icons.Other,
        title: "Create touchpoint template",
      };
    case TouchpointCreateRequestType.LANDING_PAGE:
      return {
        Icon: Icons.LandingPage,
        title: "Create web page template",
      };
    default:
      return {
        Icon: Icons.LandingPage,
        title: "Create template",
      };
  }
};

const AddNewProgramTemplateModal = ({
  isOpen,
  templateType,
  onClose,
  onSubmit,
  submitState = LoadingButtonState.Default,
}: AddNewProgramTemplateModalProps) => {
  const isEmail = templateType === TouchpointCreateRequestType.EMAIL;
  const { Icon, title } = getTemplateModalConfig(templateType);

  const { validate: validateTemplateNameUnique } = useValidateProgramTemplateName({
    enabled: isOpen,
  });

  const methods = useForm<AddNewProgramTemplateFormData>({
    defaultValues: {
      name: "",
      applyClientTheme: true,
    },
    mode: "onChange",
  });

  const { control, formState, reset } = methods;
  const { isValid, isDirty } = formState;

  const validateTemplateName = (value: string) =>
    validateTemplateNameUnique({ name: value, type: templateType });

  const handleFormSubmit = (data: AddNewProgramTemplateFormData) => {
    onSubmit?.(data, templateType);
  };

  const handleCancelClick = () => {
    reset();
    onClose();
  };

  // Reset form when modal closes after successful submission
  useEffect(() => {
    if (!isOpen && submitState === LoadingButtonState.Success) {
      reset();
    }
  }, [isOpen, submitState, reset]);

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<Icon />}
      headline={title}
      form={methods}
      onSubmit={handleFormSubmit}
      submitDisabled={!isValid || !isDirty}
      submitting={submitState === LoadingButtonState.Loading}
      submitButtonText="Create template"
      submitLoadingMessage="Creating template...">
      <Controller
        name="name"
        control={control}
        rules={{
          required: "Template name is required",
          validate: validateTemplateName,
        }}
        render={({ field, fieldState }) => (
          <TextField
            label="Template name"
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />

      {isEmail && (
        <Controller
          name="applyClientTheme"
          control={control}
          render={({ field }) => (
            <Switch
              toggleName="Enable client theme"
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      )}
    </FormDialog>
  );
};

export default AddNewProgramTemplateModal;
